// --------------------------------------------------------------
// Created On: 2021-10-11
// Author: Zachary Thomas
//
// Last Modified: 2026-10-27
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import { LOCAL_STORAGE_ASSET_LIST_DISPLAY_KEY } from "src/constants/localStorageVariables";
import React, { useState, useEffect, Fragment, useMemo } from "react";
import Card from "../../../components/Card/Card";
import AssetListItem from "./AssetListItem/AssetListItem";
import AssetListColumnHeader from "./AssetListColumnHeader/AssetListColumnHeader";
import deepCopy from "../../../utilities/deepCopy";
import calculateHighlightRuleValue from "../../../utilities/calculateHighlightRuleValue";
import calculateHighlightColor from "../../../utilities/calculateHighlightColor";
import stringIsValidNumber from "../../../utilities/stringIsValidNumber";
import isoUtcToIsoLocal from "../../../utilities/time/isoUtcToIsoLocal";
import CsvDownloadButton from "../../../components/CsvDownloadButton/CsvDownloadButton";
import styles from "./GroupListPage.module.scss";

// List of assets in current asset group with a view applied.
export default function GroupListPage(props: Props): Component {
  const NAME_COLUMN = 1;
  const NICKNAME_COLUMN = 0;
  const DISPLAY_NAME = "name";
  const DISPLAY_NICKNAME = "nickname";
  const DISPLAY_NAME_AND_NICKNAME = "nameAndNickname";
  const DISPLAY_NAME_AND_DEVICE = "nameAndDevice";
  const DISPLAY_NICKNAME_AND_DEVICE = "nicknameAndDevice";
  const DISPLAY_OPTIONS = [
    { mode: DISPLAY_NAME, label: "Display asset name" },
    { mode: DISPLAY_NICKNAME, label: "Display asset nickname" },
    { mode: DISPLAY_NAME_AND_NICKNAME, label: "Display asset name and nickname" },
    { mode: DISPLAY_NAME_AND_DEVICE, label: "Display asset name, device type, and device identifier" },
    { mode: DISPLAY_NICKNAME_AND_DEVICE, label: "Display asset nickname, device type, and device identifier" },
  ];
  const [lastCommFilterOptions, setLastCommFilterOptions] = useState<FilterOption[]>([
    { title: "Has Communicated", checked: true },
    { title: "Has Not Communicated", checked: true },
  ]);
  const [deviceTypeFilterOptions, setDeviceTypeFilterOptions] = useState<FilterOption[]>([
    { title: "Cornell Co-Pilot", checked: true },
    { title: "Cornell Pulse", checked: true },
    { title: "Seeker C", checked: true },
    { title: "Seeker Solar", checked: true },
    { title: "No Device", checked: true },
  ]);
  const [deviceIdentifierFilterOptions, setDeviceIdentifierFilterOptions] = useState<FilterOption[]>([
    { title: "Has Device", checked: true },
    { title: "No Device", checked: true },
  ]);
  const [attributeOptionsMatrix, setAttributeOptionsMatrix] = useState<FilterOption[][]>([]);
  const [assets, setAssets] = useState<ViewAsset[]>([]);
  const [units, setUnits] = useState<string[]>([]);
  const [sortColumnNumber, setSortColumnNumber] = useState<number>(1);
  const [sortAscending, setSortAscending] = useState<boolean>(true);
  const [displayMode, setDisplayMode] = useState<string>(DISPLAY_NAME_AND_NICKNAME);
  const csvData = useMemo(() => getCsvData(), [JSON.stringify(assets), JSON.stringify(props.attributes)]);
  const showName = displayMode !== DISPLAY_NICKNAME && displayMode !== DISPLAY_NICKNAME_AND_DEVICE;
  const showNickname =
    displayMode === DISPLAY_NICKNAME ||
    displayMode === DISPLAY_NAME_AND_NICKNAME ||
    displayMode === DISPLAY_NICKNAME_AND_DEVICE;
  const showDeviceColumns = displayMode === DISPLAY_NAME_AND_DEVICE || displayMode === DISPLAY_NICKNAME_AND_DEVICE;

  // Check local storage on component load to see which columns the user last chose to display in the asset list.
  useEffect(() => {
    const storedDisplayMode: string | null = localStorage.getItem(LOCAL_STORAGE_ASSET_LIST_DISPLAY_KEY);
    if (storedDisplayMode !== null && DISPLAY_OPTIONS.some((option) => option.mode === storedDisplayMode)) {
      setDisplayMode(storedDisplayMode);
    }
  }, []);

  // Setup initial filter options for columns.
  useEffect(() => {
    const attributeOptionsMatrix: FilterOption[][] = [];
    const initialFilterOptions = [
      { title: "Highlighted", checked: true },
      { title: "Not Highlighted", checked: true },
      { title: "No Data", checked: true },
    ];
    props.attributes.forEach(() => attributeOptionsMatrix.push(initialFilterOptions));
    setAttributeOptionsMatrix(attributeOptionsMatrix);
  }, [JSON.stringify(props.attributes)]);

  // Perform filtering, sorting, and applying colors to assets.
  useEffect(() => {
    const units: string[] = [];
    let assetsDeepCopy = deepCopy(props.assets);

    // Sort assets.
    assetsDeepCopy = sortAssets(sortColumnNumber, sortAscending, assetsDeepCopy);

    // Get unit and highlighting information.
    props.attributes.forEach((attribute, i) => {
      if (attribute.units !== null) {
        units.push(attribute.units);
      } else {
        units.push("");
      }

      // Apply highlighting rules to asset attributes.
      const highlightingRulesDeepCopy = deepCopy(attribute.highlightingRules).reverse();
      calculateRuleValues(highlightingRulesDeepCopy, assetsDeepCopy, i);
      applyRuleColors(highlightingRulesDeepCopy, assetsDeepCopy, i);
    });

    // Filter assets.
    // Filters on hidden device columns are ignored so they cannot silently hide assets.
    let activeDeviceTypeFilterOptions: FilterOption[] = [];
    let activeDeviceIdentifierFilterOptions: FilterOption[] = [];
    if (showDeviceColumns) {
      activeDeviceTypeFilterOptions = deviceTypeFilterOptions;
      activeDeviceIdentifierFilterOptions = deviceIdentifierFilterOptions;
    }
    assetsDeepCopy = filterAssets(
      assetsDeepCopy,
      lastCommFilterOptions,
      activeDeviceTypeFilterOptions,
      activeDeviceIdentifierFilterOptions,
      attributeOptionsMatrix
    );

    setUnits(units);
    setAssets(assetsDeepCopy);
  }, [
    JSON.stringify(props.assets),
    JSON.stringify(props.attributes),
    JSON.stringify(lastCommFilterOptions),
    JSON.stringify(deviceTypeFilterOptions),
    JSON.stringify(deviceIdentifierFilterOptions),
    JSON.stringify(attributeOptionsMatrix),
    sortColumnNumber,
    sortAscending,
    showDeviceColumns,
  ]);

  // Changes which columns describe each asset and remembers the choice for next time.
  function changeDisplayMode(newDisplayMode: string): void {
    localStorage.setItem(LOCAL_STORAGE_ASSET_LIST_DISPLAY_KEY, newDisplayMode);
    setDisplayMode(newDisplayMode);

    // Move the sorting off of any column that is no longer visible.
    const nowShowsName = newDisplayMode !== DISPLAY_NICKNAME && newDisplayMode !== DISPLAY_NICKNAME_AND_DEVICE;
    const nowShowsNickname = newDisplayMode !== DISPLAY_NAME && newDisplayMode !== DISPLAY_NAME_AND_DEVICE;
    const nowShowsDevice = newDisplayMode === DISPLAY_NAME_AND_DEVICE || newDisplayMode === DISPLAY_NICKNAME_AND_DEVICE;
    if (sortColumnNumber === NAME_COLUMN && !nowShowsName) {
      setSortColumnNumber(NICKNAME_COLUMN);
    } else if (sortColumnNumber === NICKNAME_COLUMN && !nowShowsNickname) {
      setSortColumnNumber(NAME_COLUMN);
    } else if ((sortColumnNumber === 3 || sortColumnNumber === 4) && !nowShowsDevice) {
      if (nowShowsName) {
        setSortColumnNumber(NAME_COLUMN);
      } else {
        setSortColumnNumber(NICKNAME_COLUMN);
      }
    }
  }

  // Returns the filter options for the attribute column at the given index.
  function getAttributeFilterOptions(attributeIndex: number): FilterOption[] {
    if (attributeOptionsMatrix.length > attributeIndex) {
      return attributeOptionsMatrix[attributeIndex];
    } else {
      return [];
    }
  }

  // Filter assets.
  function filterAssets(
    assets: ViewAsset[],
    lastCommFilters: FilterOption[],
    deviceTypeFilters: FilterOption[],
    deviceIdentifierFilters: FilterOption[],
    attributeMatrix: FilterOption[][]
  ): ViewAsset[] {
    let assetsShallowCopy = assets;
    // Filter based on last comm.
    lastCommFilters.forEach((filter) => {
      if (!filter.checked) {
        switch (filter.title) {
          case "Has Communicated":
            assetsShallowCopy = assetsShallowCopy.filter(
              (asset) => asset.lastComm === null || asset.lastComm.length === 0
            );
            break;
          case "Has Not Communicated":
            assetsShallowCopy = assetsShallowCopy.filter(
              (asset) => asset.lastComm !== null && asset.lastComm.length > 0
            );
            break;
        }
      }
    });
    // Filter based on device type.
    deviceTypeFilters.forEach((filter) => {
      if (!filter.checked) {
        switch (filter.title) {
          case "No Device":
            assetsShallowCopy = assetsShallowCopy.filter((asset) => asset.deviceType !== null);
            break;
          default:
            assetsShallowCopy = assetsShallowCopy.filter((asset) => asset.deviceType !== filter.title);
            break;
        }
      }
    });
    // Filter based on device identifier.
    deviceIdentifierFilters.forEach((filter) => {
      if (!filter.checked) {
        switch (filter.title) {
          case "Has Device":
            assetsShallowCopy = assetsShallowCopy.filter((asset) => asset.deviceIdentifier === null);
            break;
          case "No Device":
            assetsShallowCopy = assetsShallowCopy.filter((asset) => asset.deviceIdentifier !== null);
            break;
        }
      }
    });
    // Filter based on attributes.
    attributeMatrix.forEach((filterOptions, i) => {
      filterOptions.forEach((filter) => {
        if (!filter.checked) {
          switch (filter.title) {
            case "Highlighted":
              assetsShallowCopy = assetsShallowCopy.filter((asset) => {
                if (asset.attributes[i] !== undefined) {
                  return asset.attributes[i].colorHexCode === undefined;
                }
              });
              break;
            case "Not Highlighted":
              assetsShallowCopy = assetsShallowCopy.filter((asset) => {
                if (asset.attributes[i] !== undefined) {
                  return asset.attributes[i].colorHexCode !== undefined;
                }
              });
              break;
            case "No Data":
              assetsShallowCopy = assetsShallowCopy.filter((asset) => {
                if (asset.attributes[i] !== undefined) {
                  return asset.attributes[i].value !== null;
                }
              });
              break;
          }
        }
      });
    });
    return assetsShallowCopy;
  }

  // Sort assets.
  function sortAssets(columnNumber: number, isAscending: boolean, assets: ViewAsset[]): ViewAsset[] {
    let sortingOrder = -1;
    if (isAscending) {
      sortingOrder = 1;
    }
    switch (columnNumber) {
      case NAME_COLUMN:
      case NICKNAME_COLUMN: {
        // Name and nickname columns.
        assets.sort((a, b) => {
          let nameA = a.name;
          let nameB = b.name;
          if (columnNumber === NICKNAME_COLUMN) {
            nameA = a.nickname ?? "";
            nameB = b.nickname ?? "";
          }
          if (nameA < nameB) {
            return -1 * sortingOrder;
          } else if (nameA > nameB) {
            return 1 * sortingOrder;
          } else {
            return 0;
          }
        });
        break;
      }
      case 2: {
        // Last communications column.
        assets.sort((a, b) => {
          const lastCommA = a.lastComm;
          const lastCommB = b.lastComm;
          if (lastCommA === null) {
            return -1 * sortingOrder;
          } else if (lastCommB === null) {
            return 1 * sortingOrder;
          } else if (lastCommA < lastCommB) {
            return -1 * sortingOrder;
          } else if (lastCommA > lastCommB) {
            return 1 * sortingOrder;
          } else {
            return 0;
          }
        });
        break;
      }
      case 3: {
        // Device type column.
        assets.sort((a, b) => {
          const deviceTypeA = a.deviceType;
          const deviceTypeB = b.deviceType;
          if (deviceTypeA === null) {
            return -1 * sortingOrder;
          } else if (deviceTypeB === null) {
            return 1 * sortingOrder;
          } else if (deviceTypeA < deviceTypeB) {
            return -1 * sortingOrder;
          } else if (deviceTypeA > deviceTypeB) {
            return 1 * sortingOrder;
          } else {
            return 0;
          }
        });
        break;
      }
      case 4: {
        // Device identifier column.
        assets.sort((a, b) => {
          const deviceIdentifierA = a.deviceIdentifier;
          const deviceIdentifierB = b.deviceIdentifier;
          if (deviceIdentifierA === null) {
            return -1 * sortingOrder;
          } else if (deviceIdentifierB === null) {
            return 1 * sortingOrder;
          } else if (deviceIdentifierA < deviceIdentifierB) {
            return -1 * sortingOrder;
          } else if (deviceIdentifierA > deviceIdentifierB) {
            return 1 * sortingOrder;
          } else {
            return 0;
          }
        });
        break;
      }
      default: {
        // Attribute columns.
        if (columnNumber >= 5) {
          const attributeIndex = columnNumber - 5;
          assets.sort((a, b) => {
            if (a.attributes.length > attributeIndex && b.attributes.length > attributeIndex) {
              let attributeA;
              if (stringIsValidNumber(a.attributes[attributeIndex].value)) {
                attributeA = parseFloat(a.attributes[attributeIndex].value);
              } else {
                attributeA = a.attributes[attributeIndex].value;
              }
              let attributeB;
              if (stringIsValidNumber(b.attributes[attributeIndex].value)) {
                attributeB = parseFloat(b.attributes[attributeIndex].value);
              } else {
                attributeB = b.attributes[attributeIndex].value;
              }
              if (attributeA === null) {
                return -1 * sortingOrder;
              } else if (attributeB === null) {
                return 1 * sortingOrder;
              } else if (attributeA < attributeB) {
                return -1 * sortingOrder;
              } else if (attributeA > attributeB) {
                return 1 * sortingOrder;
              } else {
                return 0;
              }
            } else {
              return 0;
            }
          });
        }
        break;
      }
    }
    return assets;
  }

  // Calculate highlighting values for dynamic rules that require viewing a set of data.
  function calculateRuleValues(
    highlightingRules: ViewHighlightingRule[],
    assets: ViewAsset[],
    attributeIndex: number
  ): void {
    // Don't bother calculating anything if there are no highlighting rules for this attribute.
    if (highlightingRules.length === 0) {
      return;
    }

    // Get data points from each asset for the current attribute.
    const dataPoints: number[] = [];
    assets.forEach((asset) => {
      if (asset.attributes.length > attributeIndex && stringIsValidNumber(asset.attributes[attributeIndex].value)) {
        dataPoints.push(parseFloat(asset.attributes[attributeIndex].value));
      }
    });
    dataPoints.sort((a, b) => a - b);

    // Use data points to calculate dynamic highlighting rules.
    highlightingRules.forEach((highlightingRule) => {
      highlightingRule.value = calculateHighlightRuleValue(highlightingRule, dataPoints);
    });
  }

  // Apply highlighting colors to attribute values.
  function applyRuleColors(
    highlightingRules: ViewHighlightingRule[],
    assets: ViewAsset[],
    attributeIndex: number
  ): void {
    // Don't bother applying colors to anything if there are no highlighting rules for this attribute.
    if (highlightingRules.length === 0) {
      return;
    }

    // Apply colors to each asset for the current attribute.
    assets.forEach((asset) => {
      if (
        asset.attributes.length > attributeIndex &&
        asset.attributes[attributeIndex] !== null &&
        asset.attributes[attributeIndex].value !== null
      ) {
        let stringValue: string = asset.attributes[attributeIndex].value;

        // Handle boolean values.
        if (stringValue.toLowerCase() === "true") {
          stringValue = "1";
        } else if (stringValue.toLowerCase() === "false") {
          stringValue = "0";
        }

        // Handle enumeration values.
        highlightingRules.forEach((highlightingRule) => {
          if (
            highlightingRule.enumString !== null &&
            highlightingRule.enumString.toLowerCase() === stringValue.toLowerCase()
          ) {
            stringValue = highlightingRule.value.toString();
          }
        });

        if (stringIsValidNumber(stringValue)) {
          const color = calculateHighlightColor(highlightingRules, parseFloat(stringValue));
          if (color !== null) {
            asset.attributes[attributeIndex].colorHexCode = color;
          }
        }
      }
    });
  }

  // Print the current view as a table.
  function printViewTable(): void {
    const viewTable = document.getElementById("printable-view");
    if (viewTable !== null && viewTable.firstChild !== null) {
      const firstChild = viewTable.firstChild;
      const cloned = firstChild.cloneNode(true) as Element;
      document.body.appendChild(cloned);
      cloned.classList.add(styles.printable);
      window.print();
      document.body.removeChild(cloned);
    }
  }

  // Update what column and direction to sort on.
  function updateSortSettings(columnNumber: number, isAscending: boolean): void {
    setSortColumnNumber(columnNumber);
    setSortAscending(isAscending);
  }

  // Updates a filter rule to be checked or unchecked.
  function updateFilterOption(
    columnNumber: number,
    title: string,
    checked: boolean,
    mouseEvent: React.MouseEvent<HTMLElement>
  ): void {
    mouseEvent.stopPropagation();
    switch (columnNumber) {
      case 2: {
        // Last comm column.
        const lastCommFilterOptionsDeepCopy = deepCopy(lastCommFilterOptions);
        const filterOption = lastCommFilterOptionsDeepCopy.find((filterOption) => filterOption.title === title);
        if (filterOption !== undefined) {
          filterOption.checked = checked;
          setLastCommFilterOptions(lastCommFilterOptionsDeepCopy);
        }
        break;
      }
      case 3: {
        // Device type column.
        const deviceTypeFilterOptionsDeepCopy = deepCopy(deviceTypeFilterOptions);
        const filterOption = deviceTypeFilterOptionsDeepCopy.find((filterOption) => filterOption.title === title);
        if (filterOption !== undefined) {
          filterOption.checked = checked;
          setDeviceTypeFilterOptions(deviceTypeFilterOptionsDeepCopy);
        }
        break;
      }
      case 4: {
        // Device identifier column.
        const deviceIdentifierFilterOptionsDeepCopy = deepCopy(deviceIdentifierFilterOptions);
        const filterOption = deviceIdentifierFilterOptionsDeepCopy.find((filterOption) => filterOption.title === title);
        if (filterOption !== undefined) {
          filterOption.checked = checked;
          setDeviceIdentifierFilterOptions(deviceIdentifierFilterOptionsDeepCopy);
        }
        break;
      }
      default: {
        // Attribute columns.
        if (columnNumber >= 5) {
          const matrixIndex = columnNumber - 5;
          const attributeOptionsMatrixDeepCopy = deepCopy(attributeOptionsMatrix);
          if (attributeOptionsMatrixDeepCopy.length > matrixIndex) {
            const filterOption = attributeOptionsMatrixDeepCopy[matrixIndex].find(
              (filterOption) => filterOption.title === title
            );
            if (filterOption !== undefined) {
              filterOption.checked = checked;
              setAttributeOptionsMatrix(attributeOptionsMatrixDeepCopy);
            }
          }
        }
        break;
      }
    }
  }

  // Returns the data from the current view in a CSV format.
  function getCsvData(): string[][] {
    let csvData: string[][] = [];
    // Get header row.
    const headers = ["Name", "Nickname", "Device Type", "Device Identifier"];
    props.attributes.forEach((attribute) => {
      headers.push(attribute.name);
      headers.push(`${attribute.name} Units`);
    });
    headers.push(`Last Comm - ${Intl.DateTimeFormat().resolvedOptions().timeZone}`);
    csvData.push(headers);
    // Get asset data rows.
    const assetDataRows: string[][] = [];
    assets.forEach((asset) => {
      const assetDataRow: string[] = [];
      assetDataRow.push(asset.name);
      assetDataRow.push(asset.nickname ?? "");
      assetDataRow.push(asset.deviceType || "");
      assetDataRow.push(asset.deviceIdentifier || "");
      asset.attributes.forEach((attribute, i) => {
        assetDataRow.push(attribute.value);
        if (props.attributes.length > i) {
          assetDataRow.push(props.attributes[i].units);
        } else {
          assetDataRow.push("");
        }
      });
      if (asset.lastComm === null) {
        assetDataRow.push("");
      } else {
        assetDataRow.push(isoUtcToIsoLocal(asset.lastComm.replace(" ", "T")));
      }
      assetDataRows.push(assetDataRow);
    });
    csvData = [...csvData, ...assetDataRows];
    return csvData;
  }

  return (
    <div className="shadow-sm mx-4 my-2">
      {props.assets.length ? (
        <div className="table-responsive" id="printable-view">
          <table className={`${styles.table} table mb-0 pb-0`}>
            <thead className={styles.thead}>
              <tr>
                {showName && (
                  <AssetListColumnHeader
                    title="Name"
                    filterOptions={[]}
                    sortAscendingText="Sort A-Z"
                    sortDescendingText="Sort Z-A"
                    onChangeFilter={() => {
                      /* Do nothing */
                    }}
                    onSort={(isAscending) => updateSortSettings(NAME_COLUMN, isAscending)}
                  />
                )}
                {showNickname && (
                  <AssetListColumnHeader
                    title="Nickname"
                    filterOptions={[]}
                    sortAscendingText="Sort A-Z"
                    sortDescendingText="Sort Z-A"
                    onChangeFilter={() => {
                      /* Do nothing */
                    }}
                    onSort={(isAscending) => updateSortSettings(NICKNAME_COLUMN, isAscending)}
                  />
                )}
                {showDeviceColumns && (
                  <AssetListColumnHeader
                    title="Device Type"
                    filterOptions={deviceTypeFilterOptions}
                    sortAscendingText="Sort A-Z"
                    sortDescendingText="Sort Z-A"
                    onChangeFilter={(title, checked, e) => {
                      updateFilterOption(3, title, checked, e);
                    }}
                    onSort={(isAscending) => updateSortSettings(3, isAscending)}
                  />
                )}
                {showDeviceColumns && (
                  <AssetListColumnHeader
                    title="Device Identifier"
                    filterOptions={deviceIdentifierFilterOptions}
                    sortAscendingText="Sort A-Z"
                    sortDescendingText="Sort Z-A"
                    onChangeFilter={(title, checked, e) => {
                      updateFilterOption(4, title, checked, e);
                    }}
                    onSort={(isAscending) => updateSortSettings(4, isAscending)}
                  />
                )}
                {props.attributes.map((attribute, i) => (
                  <AssetListColumnHeader
                    key={attribute.attributeId}
                    title={attribute.name}
                    filterOptions={getAttributeFilterOptions(i)}
                    onChangeFilter={(title, checked, e) => {
                      updateFilterOption(5 + i, title, checked, e);
                    }}
                    onSort={(isAscending) => updateSortSettings(5 + i, isAscending)}
                  />
                ))}
                <AssetListColumnHeader
                  title="Last Comm"
                  filterOptions={lastCommFilterOptions}
                  sortAscendingText="Sort Oldest to Newest"
                  sortDescendingText="Sort Newest to Oldest"
                  onChangeFilter={(title, checked, e) => {
                    updateFilterOption(2, title, checked, e);
                  }}
                  onSort={(isAscending) => updateSortSettings(2, isAscending)}
                />

                {/* Dropdown for printing table or exporting CSV. */}
                <th className={styles.printHeader}>
                  <div className={`${styles.dropdownContainer} dropdown`}>
                    <button
                      className={`${styles.printButton} btn btn-secondary float-end`}
                      type="button"
                      data-boundary="viewport"
                      data-bs-toggle="dropdown"
                      aria-haspopup="true"
                      aria-expanded="false"
                    >
                      <i className="fa fa-fw fa-bars" />
                    </button>

                    {/* Drop down options. */}
                    <div className={`${styles.dropdownMenu} dropdown-menu dropdown-menu-end`}>
                      <button className={`${styles.option} dropdown-item`} onClick={() => printViewTable()}>
                        Print Data
                      </button>
                      <button className={`${styles.option} dropdown-item`} onClick={() => console.log(csvData)}>
                        <CsvDownloadButton className={styles.option} fileName="Asset List Data" data={csvData}>
                          Export CSV Data
                        </CsvDownloadButton>
                      </button>

                      {/* Options for which columns describe each asset. */}
                      <div className="dropdown-divider" />
                      {DISPLAY_OPTIONS.map((option) => (
                        <button
                          key={option.mode}
                          className={`${styles.option} dropdown-item`}
                          onClick={() => changeDisplayMode(option.mode)}
                        >
                          <i
                            className={`fa fa-fw fa-${displayMode === option.mode ? "check-circle" : "circle"} me-3`}
                          />
                          <span>{option.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className={styles.tableBody}>
              {assets.map((asset) => (
                <AssetListItem
                  key={asset.assetId}
                  assetId={asset.assetId}
                  name={asset.name}
                  nickname={asset.nickname}
                  deviceType={asset.deviceType}
                  deviceIdentifier={asset.deviceIdentifier}
                  attributes={asset.attributes}
                  units={units}
                  lastComm={asset.lastComm}
                  showName={showName}
                  showNickname={showNickname}
                  showDeviceColumns={showDeviceColumns}
                />
              ))}
            </tbody>
          </table>
          {assets.length === 0 && <div className={styles.tableEmptyList}>No assets to display.</div>}
        </div>
      ) : (
        <Fragment>
          {!props.loading && (
            <Card title="Assets">
              <div className={`${styles.emptyList} my-5`}>
                {props.errorMessage.length > 0 && <span>{props.errorMessage}</span>}
                {props.errorMessage.length === 0 && props.viewId > 0 && (
                  <span>There are no assets in this asset group.</span>
                )}
                {props.errorMessage.length === 0 && props.viewId <= 0 && (
                  <span>No view selected. Create a new view to see asset data.</span>
                )}
              </div>
            </Card>
          )}
        </Fragment>
      )}
    </div>
  );
}

interface Props {
  assets: ViewAsset[];
  attributes: ViewAttribute[];
  viewId: number;
  loading: boolean;
  errorMessage: string;
}

interface ViewAsset {
  assetId: number;
  name: string;
  nickname: string;
  deviceType: string;
  deviceIdentifier: string;
  attributes: ViewAssetAttribute[];
  lastComm: string | null;
}

interface ViewAssetAttribute {
  attributeId: number;
  value: string;
  colorHexCode?: string;
}

interface ViewAttribute {
  attributeId: number;
  name: string;
  units: string;
  highlightingRules: ViewHighlightingRule[];
}

interface ViewHighlightingRule {
  comparator: string;
  valueType: string;
  value: string;
  enumString: string | null;
  colorHexCode: string;
}

interface FilterOption {
  title: string;
  checked: boolean;
}
